<script setup lang="ts">
import { ref } from "vue";
import { ArrowUpTrayIcon } from "@heroicons/vue/24/outline";

const props = defineProps<{
  /** Comma-separated accept value passed to the underlying <input type=file>. */
  accept: string;
  /** Disabled while uploading. */
  disabled?: boolean;
  /** Optional helper text (e.g. "MP4, up to 5 GB"). */
  hint?: string;
}>();

const emit = defineEmits<{ file: [file: File] }>();

const inputEl = ref<HTMLInputElement | null>(null);
const dragging = ref(false);

function open() {
  if (props.disabled) return;
  inputEl.value?.click();
}

function onInputChange(event: Event) {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) emit("file", file);
  // Reset value so picking the SAME file twice still fires `change`.
  target.value = "";
}

function onDragOver(event: DragEvent) {
  if (props.disabled) return;
  event.preventDefault();
  dragging.value = true;
}

function onDragLeave() {
  dragging.value = false;
}

function onDrop(event: DragEvent) {
  if (props.disabled) return;
  event.preventDefault();
  dragging.value = false;
  const file = event.dataTransfer?.files?.[0];
  if (file) emit("file", file);
}
</script>

<template>
  <div
    data-testid="drop-zone"
    :class="[
      'relative flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-10 transition-colors cursor-pointer',
      dragging
        ? 'border-primary bg-primary/5'
        : 'border-border hover:border-text-tertiary hover:bg-bg-hover',
      disabled ? 'opacity-50 cursor-not-allowed' : '',
    ]"
    role="button"
    tabindex="0"
    :aria-disabled="disabled || false"
    @click="open"
    @keydown.enter.prevent="open"
    @keydown.space.prevent="open"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <ArrowUpTrayIcon class="h-8 w-8 text-text-tertiary" aria-hidden="true" />
    <p class="text-sm font-medium text-text-primary">
      Drop video here or
      <span class="text-primary underline-offset-2 hover:underline">click to browse</span>
    </p>
    <p v-if="hint" data-testid="drop-zone-hint" class="text-xs text-text-tertiary">
      {{ hint }}
    </p>
    <input
      ref="inputEl"
      type="file"
      data-testid="drop-zone-input"
      :accept="accept"
      :disabled="disabled || false"
      class="hidden"
      @change="onInputChange"
    />
  </div>
</template>
